import { create } from 'zustand';
import { getAutoTheme } from '@/utils/autoTheme';

export type Theme = 'light' | 'dark' | 'system' | 'auto';
type ModalType = 'none' | 'stream' | 'video' | 'profile' | 'settings' | 'donation' | 'chat' | 'confirm';

/** Named accent options → hex (Tailwind v3 500-level, matching the swatches). */
export const ACCENT_COLORS: Record<string, string> = {
    blue: '#3B82F6',
    purple: '#A855F7',
    green: '#22C55E',
    red: '#EF4444',
    orange: '#F97316',
    pink: '#EC4899',
};

const ACCENT_STORAGE_KEY = 'accent_color';

/**
 * Apply a named accent color to the daisyUI CSS variables that actually drive
 * the UI (--color-primary / --color-accent). Inline styles on <html> override
 * both the :root defaults and the [data-theme="..."] rules, so a per-user
 * accent wins over the global admin theme for logged-in users.
 */
export const applyAccentColor = (name: string) => {
    if (typeof document === 'undefined') return;
    const hex = ACCENT_COLORS[name] ?? ACCENT_COLORS.blue;
    const root = document.documentElement;
    root.style.setProperty('--color-primary', hex);
    root.style.setProperty('--color-accent', hex);
};

interface UIState {
    sidebarOpen: boolean;
    sidebarCollapsed: boolean;
    currentModal: ModalType;
    modalData: Record<string, unknown>;
    theme: Theme;
    isDarkMode: boolean;
    isLoading: boolean;
    loadingText: string;
    mobileMenuOpen: boolean;
    accentColor: string;

    toggleSidebar: () => void;
    collapseSidebar: () => void;
    expandSidebar: () => void;
    setSidebarOpen: (open: boolean) => void;
    openModal: (type: ModalType, data?: Record<string, unknown>) => void;
    closeModal: () => void;
    setTheme: (theme: Theme) => void;
    toggleTheme: () => void;
    setLoading: (loading: boolean, text?: string) => void;
    toggleMobileMenu: () => void;
    setMobileMenuOpen: (open: boolean) => void;
    setAccentColor: (color: string) => void;
}

const resolveTheme = (): Theme => {
    if (typeof window === 'undefined') return 'auto';
    return (localStorage.getItem('theme') as Theme) || 'auto';
};

const resolveIsDark = (theme: Theme): boolean => {
    switch (theme) {
        case 'dark':
            return true;
        case 'light':
            return false;
        case 'system':
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        case 'auto':
        default:
            return getAutoTheme() === 'dark';
    }
};

export const useUIStore = create<UIState>()((set, get) => ({
    sidebarOpen: true,
    sidebarCollapsed: false,
    currentModal: 'none',
    modalData: {},
    theme: resolveTheme(),
    isDarkMode: typeof window !== 'undefined' ? resolveIsDark(resolveTheme()) : true,
    isLoading: false,
    loadingText: 'Loading...',
    mobileMenuOpen: false,
    accentColor: typeof window !== 'undefined' ? (localStorage.getItem(ACCENT_STORAGE_KEY) || 'blue') : 'blue',

    // Desktop rail collapse state is independent of the mobile drawer state.
    toggleSidebar: () => {
        set((state) => ({
            sidebarCollapsed: !state.sidebarCollapsed,
        }));
    },

    collapseSidebar: () => set({ sidebarCollapsed: true }),

    expandSidebar: () => set({ sidebarCollapsed: false }),

    setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),

    openModal: (type: ModalType, data: Record<string, unknown> = {}) => set({ currentModal: type, modalData: data }),

    closeModal: () => set({ currentModal: 'none', modalData: {} }),

    setTheme: (theme: Theme) => {
        const isDark = resolveIsDark(theme);
        localStorage.setItem('theme', theme);
        document.documentElement.classList.toggle('dark', isDark);
        // Keep DaisyUI theme in sync with the dark class
        document.documentElement.setAttribute('data-theme', isDark ? 'everythingslive' : 'everythingsliveLight');
        set({ theme, isDarkMode: isDark });
    },

    toggleTheme: () => {
        const current = get().theme;
        const next = current === 'dark' ? 'light' : 'dark';
        get().setTheme(next);
    },

    setLoading: (loading: boolean, text?: string) =>
        set({ isLoading: loading, loadingText: text || 'Loading...' }),

    toggleMobileMenu: () => set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),

    setMobileMenuOpen: (open: boolean) => set({ mobileMenuOpen: open }),

    setAccentColor: (color: string) => {
        localStorage.setItem(ACCENT_STORAGE_KEY, color);
        applyAccentColor(color);
        set({ accentColor: color });
    },
}));

// Initialize theme on mount
if (typeof window !== 'undefined') {
    const theme = resolveTheme();
    const isDark = resolveIsDark(theme);
    document.documentElement.classList.toggle('dark', isDark);
    // Keep DaisyUI theme in sync with the dark class
    document.documentElement.setAttribute('data-theme', isDark ? 'everythingslive' : 'everythingsliveLight');
    // Restore the persisted accent color (the server value re-applies after fetchUser).
    applyAccentColor(localStorage.getItem(ACCENT_STORAGE_KEY) || 'blue');
}
